import React, { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Icon } from "../Icon";
import { CardGrid, CtaBand, FaqList, SectionHead, useSeo } from "../ui";
import {
  LANDING_OPTIONS,
  LANDING_PROCESS,
  OPTION_A_TERMS,
  OPTION_ORDER,
  SECRET_NOTE,
  SEGMENT_BY_SLUG,
  YOUTUBE_URL,
  contactLink,
} from "./content";
import { rememberSegment, trackVisit } from "./tracking";

/* 문구 안의 \n 을 줄바꿈으로 */
export function Lines({ text }) {
  const parts = String(text).split("\n");
  return parts.map((p, i) => (
    <React.Fragment key={i}>
      {p}
      {i < parts.length - 1 && <br />}
    </React.Fragment>
  ));
}

/* 진행 옵션 A · B · C — recommend 가 있으면 그 옵션을 맨 앞에 크게 */
export function OptionCards({ options }) {
  const rec = options && options.recommend;
  const order = rec ? [rec, ...OPTION_ORDER.filter((id) => id !== rec)] : OPTION_ORDER;
  const terms = (options && options.terms) || OPTION_A_TERMS;

  return (
    <div className={`lp-options ${rec ? "lp-options--rec" : ""}`}>
      {order.map((id) => {
        const o = LANDING_OPTIONS[id];
        const isRec = id === rec;
        const note = options && options.notes && options.notes[id];
        return (
          <article key={id} className={`lp-option ${isRec ? "lp-option--rec" : ""}`}>
            <div className="lp-option__head">
              <span className="lp-option__id" aria-hidden="true">{id}</span>
              <h3 className="lp-option__name">{o.name}</h3>
              {isRec && <span className="lp-option__badge">추천</span>}
            </div>
            <p className="lp-option__cost">{o.cost}</p>
            <p className="lp-option__cond">{o.condition}</p>
            {isRec && options.reason && <p className="lp-option__reason">{options.reason}</p>}
            {isRec && id === "A" && (
              <>
                <ul className="lp-option__terms">
                  {terms.map((t) => (
                    <li key={t}>
                      <Icon.Check width="14" height="14" aria-hidden="true" focusable="false" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                {YOUTUBE_URL && (
                  <a className="text-link lp-option__yt" href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer">
                    I.META 유튜브 채널 보기 <Icon.ArrowRight width="14" height="14" aria-hidden="true" focusable="false" />
                  </a>
                )}
              </>
            )}
            {!isRec && note && <p className="lp-option__note">{note}</p>}
            {!rec && <p className="lp-option__fit">이런 분께 — {o.fit}</p>}
          </article>
        );
      })}
    </div>
  );
}

function ProcessSteps() {
  return (
    <ol className="lp-steps">
      {LANDING_PROCESS.map((s, i) => (
        <li key={s.title} className={`lp-step ${s.optional ? "lp-step--opt" : ""}`}>
          <span className="lp-step__no">{String(i + 1).padStart(2, "0")}</span>
          <div>
            <h3 className="lp-step__title">
              {s.title}
              {s.optional && <span className="lp-step__tag">필요할 때</span>}
            </h3>
            <p className="lp-step__desc">{s.desc}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function LandingPage() {
  const { slug } = useParams();
  const seg = SEGMENT_BY_SLUG[slug];
  const ready = Boolean(seg && seg.ready);

  useSeo(
    ready
      ? { title: seg.seo.title, description: seg.seo.description, path: `/for/${slug}` }
      : {}
  );

  useEffect(() => {
    if (!ready) return;
    trackVisit(slug);
    rememberSegment(slug);
  }, [ready, slug]);

  /* 없는 분야 · 아직 준비 중인 분야는 허브로 */
  if (!ready) return <Navigate to="/for" replace />;

  const toContact = contactLink(slug);
  const rec = LANDING_OPTIONS[seg.options.recommend];

  return (
    <>
      {/* 1. 히어로 */}
      <section className="lp-hero">
        <div className="lp-hero__inner">
          <div className="kicker">{seg.hero.kicker}</div>
          <h1 className="lp-hero__title"><Lines text={seg.hero.title} /></h1>
          <p className="lp-hero__lead">{seg.hero.lead}</p>
          <div className="lp-hero__actions">
            <Link to={toContact} className="btn btn--primary btn--lg">
              아이디어 상담 문의하기
              <Icon.ArrowRight width="18" height="18" aria-hidden="true" focusable="false" />
            </Link>
          </div>
          <ul className="lp-hero__chips">
            <li>상담·검토 무료</li>
            <li>아이디어 비밀 보장</li>
            <li>추천 · {rec.name}</li>
          </ul>
        </div>
      </section>

      {/* 2. 공감 */}
      <section className="section">
        <SectionHead kicker="Pain" title="이런 고민, 하고 계시죠?" />
        <ol className="lp-pains">
          {seg.pains.map((p) => (
            <li key={p.title} className="lp-pain">
              <h3 className="lp-pain__title">{p.title}</h3>
              <p className="lp-pain__desc">{p.desc}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* 3. 해결 */}
      <section className="section">
        <SectionHead kicker="Solution" title={<Lines text={seg.solution.title} />} />
        <CardGrid items={seg.solution.items} cols={2} />
      </section>

      {/* 4. 진행 옵션 */}
      <section className="section">
        <SectionHead
          kicker="Options"
          title="진행 방식은 세 가지입니다"
          desc={`이 분야에는 ${seg.options.recommend} · ${rec.name}을 추천합니다. 최종 방식은 상담에서 함께 정합니다.`}
        />
        <OptionCards options={seg.options} />
        <p className="lp-note">제품화 파트너형 · 일반 개발형의 비용은 아이디어를 검토한 뒤 상담에서 정합니다.</p>
      </section>

      {/* 5. 제작 예시 */}
      <section className="section">
        <SectionHead
          kicker="Examples"
          title="이런 걸 만들 수 있습니다"
          desc="아래는 이 분야에서 만들 수 있는 제작 예시입니다. 목록에 없어도 편하게 물어봐 주세요."
        />
        <CardGrid items={seg.examples} />
      </section>

      {/* 6. 진행 과정 */}
      <section className="section">
        <SectionHead kicker="Process" title="문의부터 납품까지" />
        <ProcessSteps />
      </section>

      {/* 7. 왜 I.META 인가 */}
      <section className="section">
        <SectionHead kicker="Why I.META" title="왜 I.META인가요?" />
        <CardGrid items={seg.why} cols={2} />
      </section>

      {/* 8. FAQ */}
      <section className="section">
        <SectionHead kicker="FAQ" title="자주 묻는 질문" />
        <FaqList items={seg.faq} />
      </section>

      {/* 9. 최종 CTA */}
      <CtaBand title={<Lines text={seg.cta.title} />} desc={SECRET_NOTE} to={toContact} label="문의하기" />
    </>
  );
}
